import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import { RiErrorWarningFill } from 'react-icons/ri';

import postGuestCredentials from '../../api/guestCredentials';
import GuestUserStore from '../../store/guestUser';
import { IsAddressRegistered, PostalCode } from '../../store/isAddressRegistered';
import LoaderStore from '../../store/loader';
import { theme } from '../../utils/themeProvider';
import GDPRCheckbox from '../GDPRCheckbox/GDPRCheckbox';

import styles from './GuestView.module.scss';

const AddressView = dynamic(() => import('../GenericModal/AddressView/AddressView'), { ssr: false });

const REQUIRED_FIELD = 'Υποχρεωτικό πεδίο';

export default function GuestView({ buttonText = '', onSubmit }) {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [onClient, setOnClient] = useState(false);

  const [hasAcceptTerms, setHasAcceptTerms] = useState(false);
  const [address, setAddress] = useState({
    city: '',
    contactPhone: '',
    floor: '',
    nameAtBell: '',
    postalCode: '',
    street: '',
    streetNo: '',
    type: 'other',
  });
  const [hasErrorRegisterGuest, setHasErrorRegistereGuest] = useState(false);
  const router = useRouter();

  function emailValidation() {
    if (!email) {
      setEmailError(REQUIRED_FIELD);
      return false;
    }
    const _email = email.replace(/\s/g, '');
    const isValid = _email.match(
      // eslint-disable-next-line max-len
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );

    if (!isValid) {
      setEmailError('Μη έγκυρο email');
    } else {
      setEmailError('');
    }
    return isValid;
  }

  function phoneValidation() {
    if (!phone) {
      setPhoneError(REQUIRED_FIELD);
      return false;
    }
    const numberIdNoSpace = phone.replace(/\s/g, '');
    const isValid = numberIdNoSpace.match(/^\d{10}$/i);

    if (!isValid) {
      setPhoneError('Μη έγκυρο τηλέφωνο');
    } else {
      setPhoneError('');
    }
    return isValid;
  }

  function toggleTerms() {
    setHasAcceptTerms(!hasAcceptTerms);
  }

  function onClickRedirect() {
    router.push({
      pathname: '/login',
    });
  }

  async function onSubmitForm(data) {
    const isValidEmail = emailValidation();
    const isValidPhone = phoneValidation();
    if (isValidEmail && isValidPhone) {
      LoaderStore.addEvent('REGISTER_GUEST');
      const guestUserRequestData = {
        email,
        phone,
        termsAccepted: hasAcceptTerms,
      };

      const { error } = await postGuestCredentials(guestUserRequestData);
      LoaderStore.removeEvent('REGISTER_GUEST');
      if (error) {
        setErrorMessage(error);
        setHasErrorRegistereGuest(true);
        return;
      }
      const guestUserData = {
        ...data,
        contactPhone: phone,
        email,
        type: 'other',
      };
      GuestUserStore.update(guestUserData);
      PostalCode.update(data.postalCode);
      IsAddressRegistered.update(true);
      if (onSubmit) {
        return onSubmit();
      }
      router.push('/', '/');
    }
  }

  useEffect(() => {
    const guestUser$ = GuestUserStore.subscribe(guestUser => {
      if (guestUser) {
        setAddress({
          city: guestUser.city,
          contactPhone: guestUser.contactPhone,
          floor: guestUser.floor,
          nameAtBell: guestUser.nameAtBell,
          postalCode: guestUser.postalCode,
          street: guestUser.street,
          streetNo: guestUser.streetNo,
          type: 'other',
        });
        setPhone(guestUser.contactPhone);
        setEmail(guestUser.email);
      }
    });
    setOnClient(true);
    return () => {
      guestUser$.unsubscribe();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <div className={styles.detailsContainer}>
        <h2 className={styles.sectionHeader}>Στοιχεία πελάτη</h2>
        <div className={styles.inputContainer}>
          <Box sx={{ height: '5.5rem' }} className={styles.box}>
            <TextField
              label="Email"
              name="email"
              variant="outlined"
              onChange={e => setEmail(e.target.value)}
              value={email}
              onBlur={emailValidation}
              fullWidth
              required
              error={Boolean(emailError)}
              helperText={emailError}
              data-cy="email"
              InputProps={{
                endAdornment: emailError && (
                  <InputAdornment position="end">
                    <RiErrorWarningFill className={styles.waringIcon} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ height: '5.5rem' }} className={styles.box}>
            <TextField
              label="Τηλέφωνο Επικοινωνίας"
              name="phone"
              variant="outlined"
              onChange={e => setPhone(e.target.value)}
              onBlur={phoneValidation}
              value={phone}
              fullWidth
              required
              error={Boolean(phoneError)}
              helperText={phoneError}
              data-cy="userId"
              InputProps={{
                endAdornment: phoneError && (
                  <InputAdornment position="end">
                    <RiErrorWarningFill className={styles.waringIcon} />
                  </InputAdornment>
                ),
              }}
              // eslint-disable-next-line react/jsx-no-duplicate-props
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          </Box>
        </div>
        <h2 className={styles.sectionHeader}>Στοιχεία Διεύθυνσης</h2>

        {onClient && (
          <AddressView
            submitForm={onSubmitForm}
            hidePersonalData
            disableSubmit={!hasAcceptTerms}
            address={address}
            buttonText={buttonText}
          >
            <GDPRCheckbox onClickCheckbox={toggleTerms} />
          </AddressView>
        )}
      </div>
      <div className={styles.loginText}>
        Έχεις ήδη λογαριασμό;
        <div onClick={onClickRedirect}>
          <a className={styles.loginLink}> Σύνδεση</a>
        </div>
      </div>

      {hasErrorRegisterGuest && <p className={styles.errorSmallText}>{errorMessage}</p>}
    </ThemeProvider>
  );
}
